'use client';

import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useRefreshAuth, enrichUserFromLogin, type LoginResponse } from '@/hooks/api/useAuth';

interface AuthContextType {
  user: any | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  setUser: (user: any | null) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

/** Only clear JWT when we get an explicit 401 Unauthorized (expired/invalid token). Don't clear on network errors or 5xx. */
function isUnauthorizedError(error: any): boolean {
  const status = error?.response?.status ?? error?.status;
  return status === 401;
}

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<any | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const queryClient = useQueryClient();
  const { refetch: refreshAuth } = useRefreshAuth();
  const hasCheckedAuth = useRef(false);

  useEffect(() => {
    if (hasCheckedAuth.current) return;
    hasCheckedAuth.current = true;

    const checkAuth = async () => {
      const jwt = window.localStorage.getItem('jwt');

      if (jwt) {
        const cachedUser = queryClient.getQueryData(['currentUser']);
        if (cachedUser) {
          setUser(cachedUser);
          setIsLoading(false);
          return;
        }

        const result = await refreshAuth();
        if (result.isError && result.error) {
          if (isUnauthorizedError(result.error)) {
            window.localStorage.removeItem('jwt');
            queryClient.setQueryData(['currentUser'], null);
            setUser(null);
          }
          setIsLoading(false);
          return;
        }
        if (result.data?.user) {
          const data = result.data as LoginResponse;
          if (data.token) {
            window.localStorage.setItem('jwt', data.token);
          }
          const enriched = enrichUserFromLogin(data);
          setUser(enriched);
          queryClient.setQueryData(['currentUser'], enriched);
        } else {
          window.localStorage.removeItem('jwt');
          setUser(null);
        }
      }

      setIsLoading(false);
    };

    checkAuth();
  }, [queryClient, refreshAuth]);

  // Listen to React Query cache changes
  useEffect(() => {
    const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
      if (event?.query?.queryKey?.[0] === 'currentUser') {
        const cachedUser = queryClient.getQueryData(['currentUser']);
        setUser(cachedUser as any);
      }
    });

    return () => unsubscribe();
  }, [queryClient]);

  const handleSetUser = (newUser: any | null) => {
    setUser(newUser);
    if (newUser) {
      queryClient.setQueryData(['currentUser'], newUser);
    } else {
      queryClient.setQueryData(['currentUser'], null);
    }
  };

  const handleLogout = () => {
    window.localStorage.clear();
    queryClient.clear();
    setUser(null);
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    setUser: handleSetUser,
    logout: handleLogout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
